import type { BatchClient } from './batchClients';
import { cleanEvidenceAnnotations, displayLeadSummary } from './realResultAdapter';

export interface PreliminaryLeadUpgradeStep {
  key: 'data' | 'rule' | 'audit';
  label: string;
  description: string;
}

export interface PreliminaryLeadDetail {
  sourceName: string;
  sourceDate: string;
  verifiedFacts: string[];
  professionalJudgment: string;
  possibleNormalExplanation: string;
  missingConditions: string[];
  applicablePolicyName: string;
  upgradeSteps: PreliminaryLeadUpgradeStep[];
}

export type PreliminaryMaturity = 'A' | 'B' | 'C' | 'D';

export interface PreliminaryLead {
  id: string;
  enterpriseName: string;
  leadType: string;
  previewTitle: string;
  previewSummary: string;
  eventDate: string;
  triggerSignals: string[];
  potentialImpact: string[];
  possibleTaxTypes: string[];
  evidenceSummary: string;
  maturityLevel: PreliminaryMaturity;
  maturityNote: string;
  verificationGaps: string[];
  recommendedAction: string;
  updatedAt: string;
  detail: PreliminaryLeadDetail;
  /** 推荐资料（v3 missing_evidence 转换；用于企业资料需求合并） */
  recommendedMaterials?: Array<{
    name: string;
    usage: string;
    period: string;
    missingImpact: string;
  }>;
  /** 线索池或投影结果自带的核验状态，空则卡片沿用默认「待验证」 */
  verifyStatus?: string;
  evidenceLevel?: string;
  priorityLabel?: string;
  judgmentReason?: string;
  publishDate?: string;
  nextActions?: string[];
  suggestedServices?: Array<{ category: string; content: string }>;
  evidenceSources?: Array<{ name: string; type: string; date: string; note: string }>;
}

const TAX_KEYWORDS = [
  '增值税',
  '企业所得税',
  '个人所得税',
  '土地使用税',
  '车辆购置税',
  '房产税',
  '印花税',
  '关税',
  '消费税',
  '城建税',
  '车船税',
];

function truncate(text: string, max: number): string {
  const value = text.trim();
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

/** 将 LLM / adapter / v3 lead_type 归一为中文展示分类。 */
export function normalizeLeadType(raw: string): string {
  const t = (raw || '').trim();
  const lower = t.toLowerCase().replace(/[_-]+/g, '');
  // v3 英文枚举优先
  if (
    lower === 'taxrisk' ||
    lower === 'risk' ||
    lower === 'compliance' ||
    lower === 'governance'
  ) {
    return lower === 'governance' || lower === 'compliance' ? '合规筹划' : '税务风险';
  }
  if (lower === 'agency' || lower === 'service' || lower === 'advisory') {
    return '服务升级';
  }
  if (lower === 'incentive' || lower === 'taxincentive' || lower === 'benefit') {
    return '税收优惠';
  }
  if (lower === 'change' || lower === 'businesschange' || lower === 'event') {
    return '经营变化';
  }
  if (
    t.includes('优惠') ||
    lower.includes('incentive') ||
    t.includes('加计') ||
    t.includes('退税')
  ) {
    return '税收优惠';
  }
  if (t.includes('风险') || lower.includes('risk')) {
    return '税务风险';
  }
  if (
    t.includes('经营') ||
    t.includes('变化') ||
    t.includes('变更') ||
    lower.includes('change') ||
    t.includes('机会')
  ) {
    return '经营变化';
  }
  if (t.includes('合规') || t.includes('筹划') || lower.includes('govern')) {
    return '合规筹划';
  }
  if (t.includes('数字') || t.includes('服务升级') || lower.includes('agency')) {
    return '服务升级';
  }
  if (t.includes('续') || t.includes('信用')) return '税收优惠';
  if (t.includes('处罚') || t.includes('罚款') || t.includes('异常')) return '税务风险';
  if (t.includes('增资') || t.includes('注册') || t.includes('事件')) return '经营变化';
  // 纯英文残留不直接上屏
  if (/^[A-Za-z][A-Za-z0-9_-]*$/.test(t)) return '其他线索';
  return t || '其他线索';
}

function maturityFromTags(tags: string[]): PreliminaryMaturity {
  const hit = tags.find((t) => /^[ABCD]$/i.test(t));
  const m = (hit || 'C').toUpperCase();
  return (['A', 'B', 'C', 'D'].includes(m) ? m : 'C') as PreliminaryMaturity;
}

function maturityNote(level: PreliminaryMaturity): string {
  if (level === 'A') return '优先跟进';
  if (level === 'B') return '建议核验';
  if (level === 'C') return '观察信号';
  return '待评估';
}

function recommendedAction(
  level: PreliminaryMaturity,
  urgency: string,
): string {
  if (level === 'A' || urgency === 'urgent') return '发起诊断';
  if (level === 'B' || urgency === 'normal') return '补充资料';
  return '查看详情';
}

function impactByType(leadType: string): string[] {
  if (leadType === '税收优惠') return ['税负', '现金流'];
  if (leadType === '经营变化') return ['经营效率', '合规'];
  if (leadType === '税务风险') return ['合规', '现金流'];
  if (leadType === '合规筹划') return ['合规', '成本'];
  return ['合规'];
}

function resolveTaxTypes(text: string, client: BatchClient): string[] {
  const found = TAX_KEYWORDS.filter((item) => text.includes(item));
  if (found.length > 0) return found.slice(0, 3);
  const fromProfile = client.taxProfile?.taxTypes ?? [];
  if (fromProfile.length > 0) return fromProfile.slice(0, 3);
  return ['增值税', '企业所得税'];
}

function splitSignals(summary: string, title: string): string[] {
  const cleaned = cleanEvidenceAnnotations(summary);
  const parts = cleaned
    .split(/[；;。\n]+/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 4);
  if (parts.length >= 2) return parts.slice(0, 3);
  if (parts.length === 1) return [parts[0], `围绕「${title}」需结合申报与台账进一步核验`];
  return [`公开信息提示「${title}」相关信号`, '业务实质与申报口径需现场核实'];
}

function splitClauses(text: string | null | undefined, limit = 4): string[] {
  const cleaned = cleanEvidenceAnnotations(text || '');
  return cleaned
    .split(/[；;。\n]+/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 4)
    .slice(0, limit);
}

function humanizePolicyLabel(raw: string | null | undefined): string {
  const s = (raw || '').trim();
  if (!s || s === 'U' || s === '无') return '';
  if (/^PM[0-9X]+$/i.test(s)) return '相关税收政策（文号待补充）';
  return cleanEvidenceAnnotations(s) || s;
}

function shortAction(raw: string | null | undefined, fallback: string): string {
  const cleaned = cleanEvidenceAnnotations(raw || '');
  if (!cleaned) return fallback;
  const first = cleaned.split(/[；;。]/)[0]?.trim() || cleaned;
  return first.length > 18 ? `${first.slice(0, 18)}…` : first;
}

/**
 * 用浅查/全量 opportunities 生成 demo 形 PreliminaryLead 列表。
 * 优先用大模型字段：公开事实、待核事项、资料缺口、税种、政策、核验动作。
 */
export function opportunitiesToPreliminaryLeads(
  client: BatchClient,
): PreliminaryLead[] {
  const updatedAt = client.createdAt || new Date().toISOString().slice(0, 10);
  return (client.opportunities || []).map((opp, index) => {
    const leadType = normalizeLeadType(opp.category || '');
    const maturityLevel = maturityFromTags(opp.tags || []);
    const hints = opp.detailHints;
    const summary = displayLeadSummary({
      publicFacts: hints?.publicFacts,
      description: opp.description,
      markdown: hints?.markdown,
    });
    const previewSummary = truncate(summary || opp.title || '待补充摘要', 120);
    const factClauses = splitClauses(hints?.publicFacts || summary, 4);
    const signals =
      factClauses.length > 0 ? factClauses.slice(0, 3) : splitSignals(summary, opp.title);
    const assumptionClauses = splitClauses(hints?.assumptionsToVerify, 3);
    const gapFromHints = (hints?.dataGaps || [])
      .map((g) => cleanEvidenceAnnotations(g) || g.trim())
      .filter((g) => g.length >= 2)
      .slice(0, 4);
    const gaps =
      assumptionClauses.length > 0
        ? assumptionClauses
        : gapFromHints.length > 0
          ? gapFromHints
          : [
              `${leadType}相关原始凭证与业务实质需核实`,
              '历史申报口径与账面数据需逐期比对',
            ];
    const missingConditions =
      gapFromHints.length > 0
        ? gapFromHints
        : gaps.slice(0, 2);
    const taxTypes =
      hints?.taxTypes?.filter(Boolean).length
        ? hints.taxTypes.filter(Boolean).slice(0, 3)
        : resolveTaxTypes(`${opp.title}${summary}${(opp.tags || []).join('')}`, client);
    const policyName =
      (hints?.policyIds || [])
        .map(humanizePolicyLabel)
        .find(Boolean) || '待匹配适用政策';
    const sourceCount = Math.max(hints?.sourceIds?.filter(Boolean).length || 0, 1);
    const evidenceSummary = hints?.evidenceLevel
      ? `${sourceCount}项证据、${Math.min(sourceCount, 2)}个独立来源 · ${hints.evidenceLevel}`
      : `${sourceCount}项证据、${Math.min(sourceCount, 2)}个独立来源`;
    const fallbackAction = recommendedAction(maturityLevel, opp.urgency);
    const recommended = shortAction(
      hints?.nextAction || hints?.verificationAction,
      fallbackAction,
    );
    const professionalJudgment =
      cleanEvidenceAnnotations(hints?.rankReason || '') ||
      (maturityLevel === 'A' || maturityLevel === 'B'
        ? '初步判断该线索具备较高落地确定性，建议补充关键材料后升级为核心商机。'
        : '该信号尚需进一步核验，建议先观察并补齐缺失条件后再定性。');
    const normalExplanation =
      assumptionClauses[assumptionClauses.length - 1] &&
      /可能|未必|口径|误差|正常/.test(assumptionClauses[assumptionClauses.length - 1])
        ? assumptionClauses[assumptionClauses.length - 1]
        : '部分信号可能源于数据口径差异或系统映射误差，未必构成实质性结论，需调取原始凭证确认。';

    return {
      id: `${client.id}-lead-${index}`,
      enterpriseName: client.companyName,
      leadType,
      previewTitle: opp.title || '未命名线索',
      previewSummary,
      eventDate: updatedAt,
      triggerSignals: signals,
      potentialImpact: impactByType(leadType),
      possibleTaxTypes: taxTypes,
      evidenceSummary,
      maturityLevel,
      maturityNote: maturityNote(maturityLevel),
      verificationGaps: gaps,
      recommendedAction: recommended,
      updatedAt,
      detail: {
        sourceName:
          leadType === '税收优惠'
            ? '优惠政策库 · 智能匹配引擎'
            : leadType === '税务风险'
              ? '税务风险扫描 · 申报数据画像'
              : leadType === '经营变化'
                ? '工商变更公示 · 经营特征画像'
                : '企业税务画像 · 规则引擎',
        sourceDate: updatedAt,
        verifiedFacts: factClauses.length > 0 ? factClauses : signals,
        professionalJudgment,
        possibleNormalExplanation: normalExplanation,
        missingConditions,
        applicablePolicyName: policyName,
        upgradeSteps: [
          {
            key: 'data',
            label: '资料补充',
            description: `补齐${leadType}相关的原始凭证与证明材料`,
          },
        ],
      },
    };
  });
}
